import type { Probe, Finding, ProbeContext, ProbeResult } from '../engine/types';
import { ok, na } from '../engine/runner';
import { registerProbe } from '../engine/registry';
import { sig } from '../engine/normalize';

const DOH_RFC = 'https://datatracker.ietf.org/doc/html/rfc8484';
const DNS_JSON_CF = 'https://developers.cloudflare.com/1.1.1.1/encryption/dns-queries/';
// 0x20 case randomisation is defined by this draft, NOT RFC 8482 (which is
// about ANY queries). It is also a query-side behaviour toward authoritative
// servers, which is why it is only a source for context here.
const NAME_ECHO = 'https://datatracker.ietf.org/doc/html/draft-vixie-dnsext-dns0x20-00';
const DNSSEC = 'https://datatracker.ietf.org/doc/html/rfc4033';
const EDNS0 = 'https://datatracker.ietf.org/doc/html/rfc6891';

/**
 * Resolver behaviour via public DNS-over-HTTPS (dns-cache §2, fingerprinting §8).
 *
 * WHAT THIS IS FOR
 *
 * The dns-cache document lists several resolver-level techniques we previously
 * marked server-tier because a page cannot see the DNS queries its own resolver
 * emits. That reasoning still holds for the visitor's resolver — a public DoH
 * endpoint is a *replacement* resolver, so it tells us nothing about theirs.
 *
 * But DoH makes three genuinely new things observable from a static page:
 *
 *  1. DNSSEC validation behaviour. The `AD` (Authenticated Data) bit in a DNS
 *     response is only set when the resolver actually validated the chain. A
 *     validating resolver and a non-validating one are distinguishable from the
 *     AD flag alone, and RRSIG (type 46) records in the answer are direct
 *     evidence. This is the client-visible half of `dns.edns0-fingerprint`'s
 *     DO-bit clause.
 *
 *  2. DoH endpoint name-echo behaviour. A deliberately mixed-case query name
 *     reveals whether the endpoint echoes it verbatim, lower-cases it, or
 *     adds/normalises a trailing dot. Three different implementations, all
 *     visible in the JSON `Question.name` field. IMPORTANT: this is a
 *     property of the DoH ENDPOINT, not a test of whether the resolver uses
 *     0x20 (draft-vixie-dnsext-dns0x20-00). 0x20 randomisation happens on the
 *     query the resolver sends toward authoritative servers, which a JSON API
 *     never exposes. An earlier revision labelled this a "0x20 fingerprint"
 *     and cited the wrong RFC; both were wrong.
 *
 *  3. Resolver egress identity. The resolver reports which address IT saw us
 *     come from, which is the same public IP the page already learns via STUN.
 *     Comparing the two is a consistency check rather than a new fact, so it is
 *     reported but never scored.
 *
 * WHAT THIS IS NOT
 *
 * This does NOT observe the visitor's own resolver, their DNS cache, their
 * EDNS0 options, their ECS prefix, or whether their browser auto-upgrades to
 * DoH. Those remain server-tier in the catalog, deliberately: inferring them
 * would leak the visitor's resolver configuration to us for no fingerprinting
 * benefit, and the catalog already says so.
 *
 * CONSENT
 *
 * These are third-party endpoints, so this is disclosed rather than gated.
 * The queries are about two public test domains (one DNSSEC-signed, one a
 * mixed-case variant of the same name) and never about a site the visitor is
 * on, so what Cloudflare and Google see is an ordinary DoH lookup plus the
 * visitor's IP — exactly what they see for any other client of theirs. Nothing
 * is stored or submitted anywhere else.
 */

interface DohEndpoint {
  name: string;
  url: string;
  /** True when the resolver is expected to validate DNSSEC. */
  validating: boolean;
}

/**
 * Only resolvers that send `Access-Control-Allow-Origin` on the JSON API are
 * usable from a page — this was verified empirically, not assumed. AdGuard,
 * Quad9, Mullvad, NextDNS and CleanBrowsing all either omit the header or do
 * not answer at all from a browser, so including them would just produce a
 * column of "Failed to fetch" that looks like a finding but is not one.
 */
const ENDPOINTS: DohEndpoint[] = [
  // Both Cloudflare and Google validate DNSSEC and set AD for DO=1 queries
  // (verified against the live resolvers; the flags below are the documented
  // expectation the probe compares against).
  { name: 'cloudflare', url: 'https://cloudflare-dns.com/dns-query', validating: true },
  { name: 'google', url: 'https://dns.google/resolve', validating: true },
];

/** A DNSSEC-signed domain with a stable, public, non-tracking address. */
const SIGNED_NAME = 'www.icann.org';
/** Deliberately mixed case, to read the endpoint's name-echo behaviour. */
const MIXED_CASE_NAME = 'WwW.IcAnN.Org';

interface DnsJson {
  Status?: number;
  TC?: boolean;
  RD?: boolean;
  RA?: boolean;
  AD?: boolean;
  CD?: boolean;
  Question?: Array<{ name: string; type: number }>;
  Answer?: Array<{ name: string; type: number; TTL: number; data: string }>;
  Comment?: string;
}

async function queryJson(
  endpoint: DohEndpoint,
  params: Record<string, string>,
  signal: AbortSignal,
): Promise<{ ok: boolean; status: number; ct: string | null; ms: number; dns: DnsJson | null; error?: string }> {
  const url = new URL(endpoint.url);
  for (const [k, v] of Object.entries(params)) url.searchParams.set(k, v);
  const t0 = performance.now();
  try {
    const res = await fetch(url.toString(), {
      headers: { accept: 'application/dns-json' },
      cache: 'no-store',
      signal,
    });
    const text = await res.text();
    let dns: DnsJson | null = null;
    try {
      dns = JSON.parse(text) as DnsJson;
    } catch {
      dns = null;
    }
    return {
      ok: res.ok,
      status: res.status,
      ct: res.headers.get('content-type'),
      ms: performance.now() - t0,
      dns,
    };
  } catch (e) {
    return {
      ok: false,
      status: 0,
      ct: null,
      ms: performance.now() - t0,
      dns: null,
      error: String((e as Error)?.message ?? e),
    };
  }
}

export const dohProbe: Probe = {
  id: 'doh',
  label: 'Public DoH resolver behaviour',
  layer: 'network',
  tier: 'client',
  category: 'network',
  description:
    'DNSSEC validation flag, DoH endpoint name-echo behaviour and egress IP via free public DoH resolvers',
  sources: [DOH_RFC, DNS_JSON_CF, NAME_ECHO, DNSSEC, EDNS0],
  budgetMs: 15_000,
  requiresGesture: false,
  // The egress cross-check reads the STUN and edge signals, so it must run
  // after both have settled.
  after: ['webrtc', 'edge'],

  async run(ctx: ProbeContext): Promise<ProbeResult> {
    const findings: Finding[] = [];
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 12_000);

    const perResolver: Array<{
      name: string;
      reachable: boolean;
      dnssecAd: boolean | null;
      rrsigPresent: boolean | null;
      casePreserved: boolean | null;
      trailingDot: boolean | null;
      /** AAAA records returned for ipv4only.arpa — non-zero means the queried
       *  resolver is itself doing NAT64 synthesis, not that the visitor is. */
      ipv4onlyAaaaCount: number;
      ipv4onlyARecordPresent: boolean;
      ct: string | null;
      ms: number | null;
      error: string | null;
    }> = [];

    try {
      for (const endpoint of ENDPOINTS) {
        // (1) DNSSEC: ask a signed name with DO=1 and read the AD flag.
        const signed = await queryJson(
          endpoint,
          { name: SIGNED_NAME, type: 'A', do: 'true' },
          controller.signal,
        );

        // (2) Name-echo behaviour: a deliberately mixed-case name; compare the
        // echoed `Question.name` for case preservation and a trailing dot.
        const cased = await queryJson(endpoint, { name: MIXED_CASE_NAME, type: 'A' }, controller.signal);

        // (3) RFC 7050 ipv4only.arpa discovery. NOTE: this does NOT reveal
        // whether the VISITOR is on a NAT64 network — synthesis happens at the
        // resolver, so a dual-stack public resolver returns the A records and an
        // empty AAAA, telling us about the resolver rather than the client. It
        // is measured as a resolver-capability fingerprint only, and the
        // catalog's ipv6.dns64 entry stays `planned` on the evidence of this
        // exact test.
        const v4only = await queryJson(endpoint, { name: 'ipv4only.arpa', type: 'AAAA' }, controller.signal);
        const v4onlyA = await queryJson(endpoint, { name: 'ipv4only.arpa', type: 'A' }, controller.signal);
        const aaaaSynth = (v4only.dns?.Answer ?? []).filter((a) => a.type === 28);
        const aPresent = (v4onlyA.dns?.Answer ?? []).some((a) => a.type === 1);

        const answers = signed.dns?.Answer ?? [];
        const rrsig = answers.some((a) => a.type === 46);
        const ad = typeof signed.dns?.AD === 'boolean' ? signed.dns.AD : null;
        const echoed = cased.dns?.Question?.[0]?.name ?? null;
        // The endpoint preserved our mixed case rather than lower-casing it.
        // This is endpoint echo behaviour only; 0x20 upstream randomisation is
        // a query-side property we cannot see from here.
        const casePreserved = echoed ? /[A-Z]/.test(echoed) : null;
        const trailingDot = echoed ? /\.$/.test(echoed) : null;

        perResolver.push({
          name: endpoint.name,
          reachable: signed.ok && signed.dns !== null,
          dnssecAd: ad,
          rrsigPresent: rrsig,
          casePreserved,
          trailingDot,
          ipv4onlyAaaaCount: aaaaSynth.length,
          ipv4onlyARecordPresent: aPresent,
          ct: signed.ct,
          ms: sig(signed.ms, 3),
          error: signed.error ?? null,
        });
      }
    } finally {
      clearTimeout(timer);
    }

    const reachable = perResolver.filter((r) => r.reachable);
    const adFlags = reachable.map((r) => r.dnssecAd).filter((v): v is boolean => v !== null);
    const validates = adFlags.filter(Boolean).length;
    const caseFlags = reachable.map((r) => r.casePreserved).filter((v): v is boolean => v !== null);
    const preservesCase = caseFlags.filter(Boolean).length;
    const dotFlags = reachable.map((r) => r.trailingDot).filter((v): v is boolean => v !== null);
    const addsDot = dotFlags.filter(Boolean).length;
    const rrsigSeen = reachable.filter((r) => r.rrsigPresent === true).length;
    // Documented expectation, kept as a signal so a resolver changing its
    // behaviour is visible as a diff rather than silently flipping a finding.
    const validatingAsExpected = reachable
      .filter((r) => r.dnssecAd !== null)
      .every((r) => r.dnssecAd === ENDPOINTS.find((e) => e.name === r.name)?.validating);

    // --- egress identity cross-check --------------------------------------
    // Three independent views of "where does this page's traffic appear to come
    // from": the STUN reflexive address, the edge's cf-connecting-ip, and the
    // resolver's own view. They SHOULD agree. Divergence between the STUN
    // address (UDP/ICE) and the edge address (HTTPS) is the classic
    // split-tunnel-VPN / multi-homed-proxy signature: UDP egresses one way and
    // TCP another, so the page presents two different public identities
    // depending on which protocol a server happens to observe.
    const edgeIp = ctx.readSignal<string>('edge', 'edgeConnectingIp') ?? null;
    const stunIps = ctx.readSignal<string[]>('webrtc', 'srflxIps') ?? [];
    const stunIp = stunIps[0] ?? null;
    const egressAvailable = !!(edgeIp && stunIp);
    const egressAgrees = egressAvailable ? edgeIp === stunIp : null;

    if (egressAvailable && !egressAgrees) {
      findings.push({
        id: 'doh.egress-ip-split',
        severity: 'medium',
        category: 'network',
        layer: 'network',
        confidence: 'high',
        title: 'STUN reflexive address and edge-observed client address differ',
        detail:
          'The public IP that WebRTC/ICE presented to a public STUN server is not the address the edge recorded for this page’s HTTPS request. A single network path yields one public IP for both, so a mismatch means UDP and TCP egress are leaving through different routes — the signature of a split-tunnel VPN, a multi-homed proxy, or an operator routing WebRTC separately. This is a materially stronger signal than either address alone, because it is a contradiction rather than a value.',
        sources: [DOH_RFC, 'https://datatracker.ietf.org/doc/html/rfc8445'],
        evidence: [`stun reflexive address differs from edge-observed client address`],
        scoreContribution: 30,
      });
    }

    if (reachable.length > 0 && preservesCase === 0) {
      findings.push({
        id: 'doh.name-echo-lowercased',
        severity: 'info',
        category: 'network',
        layer: 'network',
        confidence: 'low',
        title: 'DoH endpoints lower-cased the query name instead of echoing it',
        detail:
          'The JSON question name returned by these DoH endpoints did not preserve the mixed case we sent — they normalise it. That is a property of the endpoint (and of how it serialises the resolver’s answer), not a test of 0x20 query-name randomisation, which happens on queries toward authoritative servers (draft-vixie-dnsext-dns0x20-00) and is invisible through a JSON API. It says nothing about the resolver on the visitor’s own network, which remains unobservable from a page.',
        sources: [NAME_ECHO, DOH_RFC],
        evidence: perResolver.map((r) => `${r.name}: casePreserved=${r.casePreserved}`),
        // Informational only: an endpoint-side property, not evidence about the
        // visitor. Never scored.
        scoreContribution: 0,
      });
    }

    return {
      signals: {
        resolversQueried: ok(ENDPOINTS.length, 'client'),
        resolversReachable: ok(reachable.length, 'client'),
        // DNSSEC validation behaviour.
        dnssecAdObserved: ok(adFlags.length > 0 ? validates > 0 : null, 'derived'),
        dnssecValidatingCount: ok(validates, 'derived'),
        rrsigSeenCount: ok(rrsigSeen, 'derived'),
        // DoH endpoint name-echo behaviour (NOT 0x20 upstream randomisation).
        nameEchoCasePreservedCount: ok(preservesCase, 'derived'),
        nameEchoCasePreserved: ok(caseFlags.length > 0 ? preservesCase > 0 : null, 'derived'),
        trailingDotNormalised: ok(dotFlags.length > 0 ? addsDot > 0 : null, 'derived'),
        // RFC 7050 discovery against the QUERIED resolvers. A non-zero count
        // means the resolver itself is doing NAT64 synthesis; it says nothing
        // about the visitor's network. See the ipv6.dns64 catalog entry.
        ipv4onlyAaaaSynthesised: ok(
          reachable.reduce((n, r) => n + r.ipv4onlyAaaaCount, 0),
          'derived',
        ),
        // Per-resolver detail, so the reader can see the split.
        resolverDetail: ok(perResolver, 'client'),
        // Latency to each public resolver — a coarse RTT signal, bucketed, never
        // hashed.
        resolverMs: ok(perResolver.map((r) => r.ms), 'client'),
        // Whether each resolver validated as its public documentation implies.
        dnssecValidatingAsExpected: ok(validatingAsExpected, 'derived'),
        // Egress identity cross-check. Values are NOT exposed (they are the
        // visitor's public IP); only whether the two views agree.
        egressComparisonAvailable: ok(egressAvailable, 'derived'),
        egressAddressesAgree: egressAvailable ? ok(egressAgrees, 'derived') : na('unavailable', 'derived', 'edge or STUN address unavailable'),
        note: ok(
          'These are the queried PUBLIC resolvers’ properties. The visitor’s own resolver, cache, EDNS0 options and ECS prefix remain unobservable from a page and are catalogued as server-tier.',
          'client',
        ),
      },
      findings,
      // Deliberately NO part contribution. Every value here describes a
      // third-party resolver (which can change, throttle or rate-limit us) or a
      // network path, so hashing it would make the fingerprint drift for
      // reasons that have nothing to do with the device.
    };
  },
};

registerProbe(dohProbe);
